/**
 * AI Insight Card — shows type badge, urgency, reasoning, suggested actions, and feedback.
 * Confidence is always shown as High/Medium/Low, never a raw decimal.
 *
 * When `enableNav` is true and the insight carries `entity_type` / `entity_id`,
 * the title becomes a link that routes to the underlying record. Cross-entity
 * surfaces (MyDay, the AI Assistant overview) opt in; entity-scoped surfaces
 * (Project / Portfolio / Task workspaces) leave it off — the user is already
 * on the record.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThumbsUp, ThumbsDown, ChevronDown, ChevronUp, ArrowUpRight } from 'lucide-react'
import { INSIGHT_TYPE, URGENCY, type InsightType, type Urgency } from './tokens'

interface Insight {
  id: number
  type: string
  title: string
  content?: string | null
  reasoning?: string | null
  suggested_actions?: string[] | null
  confidence?: number | string | null
  urgency?: string | null
  feedback_score?: number | null
  entity_type?: string | null
  entity_id?: number | null
}

interface Props {
  insight: Insight
  onFeedback?: (id: number, score: 1 | -1) => void
  className?: string
  enableNav?: boolean
}

function insightRoute(insight: Insight): string | null {
  if (!insight.entity_type || !insight.entity_id) return null
  switch (insight.entity_type) {
    case 'project':
      return `/project/${insight.entity_id}`
    case 'task':
      return `/task/${insight.entity_id}`
    case 'portfolio':
      return `/portfolio/${insight.entity_id}`
    case 'program':
      return `/program/${insight.entity_id}`
    case 'risk':
    case 'issue':
      return `/risks`
    default:
      return null
  }
}

function confidenceLabel(c: number | string | null | undefined): string {
  if (!c) return 'Medium'
  if (typeof c === 'string') return c
  if (c >= 0.75) return 'High'
  if (c >= 0.45) return 'Medium'
  return 'Low'
}

export function InsightCard({ insight, onFeedback, className = '', enableNav = false }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [voted, setVoted] = useState<1 | -1 | null>(insight.feedback_score as 1 | -1 | null ?? null)
  const navigate = useNavigate()

  const typeToken = INSIGHT_TYPE[(insight.type as InsightType)] ?? INSIGHT_TYPE.recommendation
  const urgencyKey = (insight.urgency ?? 'medium') as Urgency
  const urgencyToken = URGENCY[urgencyKey] ?? URGENCY.medium
  const confidence = confidenceLabel(insight.confidence)
  const route = enableNav ? insightRoute(insight) : null

  const handleVote = (score: 1 | -1) => {
    if (voted === score) return
    setVoted(score)
    onFeedback?.(insight.id, score)
  }

  return (
    <div className={`rounded-lg border bg-card p-4 shadow-sm ${className}`} role="article">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium px-2 py-0.5 rounded border ${typeToken.badge}`}>
            {typeToken.label}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded border ${urgencyToken.badge}`}>
            {urgencyToken.label}
          </span>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          aria-label={expanded ? 'Collapse insight' : 'Expand insight'}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Title — clickable when cross-entity nav is enabled and the insight
          has an entity_type / entity_id that maps to a known route. */}
      {route ? (
        <button
          type="button"
          onClick={() => navigate(route)}
          className="group text-left mb-1 text-sm font-semibold text-foreground hover:text-[#486581] focus:outline-none focus:underline inline-flex items-start gap-1"
          aria-label={`Open ${insight.entity_type ?? 'record'} for ${insight.title}`}
        >
          <span>{insight.title}</span>
          <ArrowUpRight className="w-3.5 h-3.5 mt-0.5 opacity-60 group-hover:opacity-100 flex-shrink-0" aria-hidden="true" />
        </button>
      ) : (
        <p className="text-sm font-semibold text-foreground mb-1">{insight.title}</p>
      )}

      {/* Reasoning (always shown, truncated when collapsed) */}
      {(insight.reasoning || insight.content) && (
        <p className={`text-sm text-muted-foreground mb-3 ${expanded ? '' : 'line-clamp-2'}`}>
          {insight.reasoning || insight.content}
        </p>
      )}

      {/* Suggested actions — only when expanded */}
      {expanded && insight.suggested_actions && insight.suggested_actions.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Suggested actions</p>
          <ul className="space-y-1">
            {insight.suggested_actions.map((action, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <span className="text-muted-foreground mt-0.5">→</span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer: confidence + feedback */}
      <div className="flex items-center justify-between pt-2 border-t mt-2">
        <span className="text-xs text-muted-foreground">
          Confidence: <span className="font-medium text-foreground">{confidence}</span>
        </span>
        {onFeedback && (
          <div className="flex items-center gap-1" aria-label="Was this useful?">
            <span className="text-xs text-muted-foreground mr-1">Useful?</span>
            <button
              onClick={() => handleVote(1)}
              className={`p-1 rounded hover:bg-muted transition-colors ${voted === 1 ? 'text-green-600' : 'text-muted-foreground'}`}
              aria-label="Thumbs up"
              aria-pressed={voted === 1}
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleVote(-1)}
              className={`p-1 rounded hover:bg-muted transition-colors ${voted === -1 ? 'text-red-600' : 'text-muted-foreground'}`}
              aria-label="Thumbs down"
              aria-pressed={voted === -1}
            >
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
